import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminAuditEvents, isApiConfigured } from "../apiClient";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatJson(value) {
  if (value == null) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function actorLabel(event) {
  if (event.actorEmail) return event.actorEmail;
  if (event.actorType === "admin" && event.actorAdminId) return `Admin #${event.actorAdminId}`;
  if (event.actorType === "user" && event.actorUserId) return `User #${event.actorUserId}`;
  if (event.actorType === "system") return "System";
  return "—";
}

export default function AdminAuditPage({ currentUser, authToken, onSignOut }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const filters = useMemo(
    () => ({
      action: searchParams.get("action") || "",
      entityType: searchParams.get("entityType") || "",
      actorAdminId: searchParams.get("actorAdminId") || "",
      actorUserId: searchParams.get("actorUserId") || "",
      subjectUserId: searchParams.get("subjectUserId") || "",
    }),
    [searchParams]
  );

  const loadEvents = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setError("Not signed in.");
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getAdminAuditEvents(t, { ...filters, limit: 100 });
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load audit log.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, filters]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    const trimmed = String(value || "").trim();
    if (trimmed) next.set(key, trimmed);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin" className="jw-admin__back">
        ← Back to home
      </Link>
      <h1 className="jw-admin__title">Audit log</h1>
      <p className="jw-admin__lede">
        Who changed what — admin config, user accounts, and job posts. Kept for 24 months.
      </p>

      <div className="jw-admin__toolbar jw-admin-audit-filters">
        <label className="jw-admin__filter-label" htmlFor="audit-filter-action">
          Action
        </label>
        <input
          id="audit-filter-action"
          className="jw-admin-rates__input jw-admin-rates__input--wide"
          value={filters.action}
          placeholder="e.g. pricing.job_prices.save"
          onChange={(e) => updateFilter("action", e.target.value)}
        />
        <label className="jw-admin__filter-label" htmlFor="audit-filter-entity">
          Entity
        </label>
        <input
          id="audit-filter-entity"
          className="jw-admin-rates__input"
          value={filters.entityType}
          placeholder="job_pricing"
          onChange={(e) => updateFilter("entityType", e.target.value)}
        />
        <label className="jw-admin__filter-label" htmlFor="audit-filter-subject">
          Subject user id
        </label>
        <input
          id="audit-filter-subject"
          className="jw-admin-rates__input"
          value={filters.subjectUserId}
          placeholder="User id"
          onChange={(e) => updateFilter("subjectUserId", e.target.value)}
        />
        <button type="button" className="jw-admin__refresh" onClick={() => void loadEvents()}>
          Refresh
        </button>
      </div>

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="jw-admin__muted">Loading audit log…</p> : null}

      {!loading && events.length === 0 ? (
        <p className="jw-admin__muted">No matching activity yet.</p>
      ) : null}

      {!loading && events.length > 0 ? (
        <div className="jw-admin-rates__table-wrap">
          <table className="jw-admin-rates__table jw-admin-audit-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Action</th>
                <th scope="col">Summary</th>
                <th scope="col">Subject</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const expanded = expandedId === event.id;
                return (
                  <Fragment key={event.id}>
                    <tr key={event.id}>
                      <td>{formatWhen(event.createdAt)}</td>
                      <td>{actorLabel(event)}</td>
                      <td>
                        <code>{event.action}</code>
                      </td>
                      <td>{event.summary}</td>
                      <td>
                        {event.subjectUserId ? (
                          <Link to={`/admin/users/${event.subjectUserId}`}>User #{event.subjectUserId}</Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="jw-admin__refresh"
                          onClick={() => setExpandedId(expanded ? "" : event.id)}
                        >
                          {expanded ? "Hide" : "Details"}
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="jw-admin-audit-detail-row">
                        <td colSpan={6}>
                          <div className="jw-admin-audit-detail">
                            <div>
                              <p className="jw-admin-audit-detail__label">Before</p>
                              <pre>{formatJson(event.beforeState)}</pre>
                            </div>
                            <div>
                              <p className="jw-admin-audit-detail__label">After</p>
                              <pre>{formatJson(event.afterState)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </AdminShell>
  );
}
